import React from "react";
import styled from "styled-components";

import { LibraryView } from "common/constants";
import { ActionPermissionsObject } from "common/interfaces";
import { Divider } from "js/Components/Divider";
import UpgradeIcon from "js/Components/UpgradeIcon";
import WorkspaceController from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import {
    LibraryNavigationView,
    navigationSelectedClass,
    StyledNavigationIcon,
    StyledNavigationSideBarItem,
} from "js/editor/PresentationLibrary/libraryNavigationItems";
import { LibraryTeamSettingsNavigation } from "js/editor/PresentationLibrary/LibraryTeamSettingsNavigation";
import { t } from "js/i18n";
import { TeamResourceTypes } from "js/react/views/TeamResources/TeamResourceTypes";

const TEAM_LIBRARIES_LABEL = "Team Resources";

const NavigationSectionLabel = styled.div`
    color: #ccc;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    padding: 10px;
    cursor: pointer;

    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`;

const TeamLibrariesLabel = styled.span`
    display: flex;
    align-items: center;
    gap: 6px;
`;

type TeamLibrariesNavItemsProps = {
    teamPane?: string;
};

function TeamLibrariesNavItems({ teamPane }: TeamLibrariesNavItemsProps) {
    return (
        <>
            <StyledNavigationSideBarItem
                className={navigationSelectedClass(teamPane === TeamResourceTypes.TEAM_THEMES)}
                onClick={() => AppController.showTeamResources({ pane: TeamResourceTypes.TEAM_THEMES })}
                data-test-id="sidebar-nav-team-themes"
            >
                <StyledNavigationIcon>color_lens</StyledNavigationIcon>
                {t("Shared Brand Kits")}
            </StyledNavigationSideBarItem>

            <StyledNavigationSideBarItem
                className={navigationSelectedClass(teamPane === TeamResourceTypes.PRESENTATION_TEMPLATES)}
                onClick={() => AppController.showTeamResources({ pane: TeamResourceTypes.PRESENTATION_TEMPLATES })}
                data-test-id="sidebar-nav-team-templates"
            >
                <StyledNavigationIcon>video_library</StyledNavigationIcon>
                {t("Team Templates")}
            </StyledNavigationSideBarItem>

            <StyledNavigationSideBarItem
                className={navigationSelectedClass(
                    teamPane === TeamResourceTypes.SLIDE_TEMPLATES ||
                    teamPane === TeamResourceTypes.SHARED_SLIDES ||
                    teamPane === TeamResourceTypes.SLIDES
                )}
                onClick={() => AppController.showTeamResources({ pane: TeamResourceTypes.SLIDES })}
                data-test-id="sidebar-nav-team-slides"
            >
                <StyledNavigationIcon>library_add</StyledNavigationIcon>
                {t("Team Slides")}
            </StyledNavigationSideBarItem>

            <StyledNavigationSideBarItem
                className={navigationSelectedClass(
                    teamPane === TeamResourceTypes.TEAM_ASSETS ||
                    teamPane === TeamResourceTypes.TEAM_ASSETS_IMAGES ||
                    teamPane === TeamResourceTypes.SHARED_ASSETS_LOGOS
                )}
                onClick={() => AppController.showTeamResources({ pane: TeamResourceTypes.TEAM_ASSETS })}
                data-test-id="sidebar-nav-team-assets"
            >
                <StyledNavigationIcon>photo_library</StyledNavigationIcon>
                {t("Team Assets")}
            </StyledNavigationSideBarItem>

            <LibraryTeamSettingsNavigation teamPane={teamPane} />
        </>
    );
}

type TeamLibrariesNavigationProps = {
    teamPane?: string;
};

/** Team plan: shared resources already available — always expanded, no upgrade CTA. */
function TeamLibrariesNavigation({ teamPane }: TeamLibrariesNavigationProps) {
    return (
        <>
            <Divider margin={0} style={{ marginTop: 5 }} color="#444" />
            <NavigationSectionLabel data-test-id="sidebar-nav-team-libraries-header">
                <TeamLibrariesLabel>
                    <span>{t(TEAM_LIBRARIES_LABEL)}</span>
                </TeamLibrariesLabel>
            </NavigationSectionLabel>
            <TeamLibrariesNavItems teamPane={teamPane} />
        </>
    );
}

type DiscoverTeamPlanNavigationProps = {
    libraryView: LibraryNavigationView;
};

/** Basic/Pro plan: shared resources not on plan — CTA navigates to Discover Team Plan pane. */
function DiscoverTeamPlanNavigation({ libraryView }: DiscoverTeamPlanNavigationProps) {
    const isSelected = libraryView === LibraryView.DISCOVER_TEAM_PLAN;

    const onHeaderClick = () => {
        if (isSelected) {
            return;
        }

        AppController.showDiscoverTeamPlan();
    };

    return (
        <>
            <Divider margin={5} color="#444" />
            <StyledNavigationSideBarItem
                className={navigationSelectedClass(isSelected)}
                data-test-id="sidebar-nav-team-libraries-header"
                onClick={onHeaderClick}
            >
                <UpgradeIcon />
                {t("Team Resources")}
            </StyledNavigationSideBarItem>
        </>
    );
}

type LibraryTeamLibrariesNavigationProps = {
    libraryView: LibraryNavigationView;
    teamPane?: string;
};

export function LibraryTeamLibrariesNavigation({ libraryView, teamPane }: LibraryTeamLibrariesNavigationProps) {
    // Plan-gated only: Basic/Pro see Discover; Team+ see Team Resources.
    // Guests never reach here — shouldShowTeamResources() hides this section for guests.
    const planIncludesSharedResources = !!WorkspaceController.planPermissions[ActionPermissionsObject.SHARED_RESOURCES];

    if (!planIncludesSharedResources) {
        return <DiscoverTeamPlanNavigation libraryView={libraryView} />;
    }

    return <TeamLibrariesNavigation teamPane={teamPane} />;
}
